import { QuoteStep } from "../components/QuoteStepperDisplay";
import { Branding, LLMResponse, PageState, Quote, QuoteRequest, UserDetails, UserSetupInfo } from "../types";
import api from "./api";

type Params = {
    [key: string]: any;
};

class QuoteService {
    private baseUrl = "/quoter";

    initQuote = async (initParams: Params): Promise<InitResponse> => {
        try {
            const { data } = await api.post(this.baseUrl + "/init", initParams);
            return data as InitResponse;
        } catch (error) {
            console.error('Error initializing quote:', error);
            return null;
        }
    }

    loadNewContent = async (action: string, params: Params): Promise<LoadNewContentResponse> => {
        try {
            params["action"] = action
            const { data } = await api.post(this.baseUrl + "/load-new-content", params);
            return data as LoadNewContentResponse;
        } catch (error) {
            console.error('Error Loading quote content:', error);
            return null;
        }
    }

    getQuote = async (params: Params): Promise<GetQuoteResponse> => {
        try {
            const { data } = await api.post(this.baseUrl + "/quote", params);
            return data as GetQuoteResponse;
        } catch (error) {
            console.error('Error Loading quote content:', error);
            return null;
        }
    }

    sendQuoteParameter = async (params: Params): Promise<SendQuoteParameterResponse> => {
        try {
            const { data } = await api.post(this.baseUrl + "/quote-parameter", params);
            return data as SendQuoteParameterResponse;
        } catch (error) {
            console.error('Error sending quote', error);
            return null;
        }
    }

    getFileteredProfessions = async (filter: string, onlyShowBindable: boolean): Promise<string[]> => {
        try {
            const { data } = await api.get(this.baseUrl + "/filtered-professions", { params: {filter, onlyShowBindable}})
            return data as string[];
        } catch (error) {
            console.error("Error fetching professions:", error);
            return ["No Matches Found"];
        }
    };

    requestProfession = async (profession: string, email: string): Promise<boolean> => {
        try {
            const response = await api.post(this.baseUrl + "/request-profession", { profession, email });
            return response.status === 200;
        } catch (error) {
            console.error("Error requesting profession:", error);
            return false;
        }
    };

    getFilteredCompanies = async (companyName): Promise<{ [key: string]: CompanyData }> => {
        try {
            const { data } = await api.get(this.baseUrl + "/filtered-companies", { params: {filter: companyName} }); 
            let mappedCompanyData = {};
            data.forEach(function (el) {
                if (el["company_status"] === "active") {
                    mappedCompanyData[el["title"]] = {
                        name: el["title"],
                        number: el["company_number"],
                        address: el["address"],
                    };
                }
            });
            return mappedCompanyData;
        } catch (error) {
            console.error("Error fetching companies:", error);
            return {};
        }
    };

    sendReferralPreferences = async (referral: boolean) => {
        try {
            const { data } = await api.post(this.baseUrl + "/referral", { referral });
            return data;
        } catch (error) {
            console.error("Error submitting referral preference:", error);
            return null;
        }
    }

    changeInstallation = async (installation: string) => {
        try {
            await api.post(this.baseUrl + "/installation", { installation });
        } catch (error) {
            console.error("Error changing installation:", error);
        }
    }

    getAllPages = async () => {
        try {
            const { data } = await api.get(this.baseUrl + "/page");
            return data;

        } catch (error) {
            console.error("Error fetching pages:", error);
            return null
        }
    }

    navigateToPage = async (index: number) => {
        try {
            const { data } = await api.post(this.baseUrl + "/page", {index});
            return data;
        } catch (error) {
            console.error("Error navigating to page:", error);
            return null
        }
    }

    getQuoteSteps = async () => {
        try {
            const { data } = await api.get(this.baseUrl + "/quote-steps"); 
            return data.quoteSteps;
        } catch (error) {
            console.error("Error fetching quote steps:", error);
            return {};
        }
    }

    getAddress = async (addressId: string): Promise<Address> => {
        try {
            const { data } = await api.get(this.baseUrl + "/address", { params: { addressid: addressId } }); 
            return data;
        } catch (error) {
            console.error("Error fetching address", error);
            return null;
        }
    }

    getAddressSuggestion = async (postcode: string): Promise<AddressSuggestion[]> => {
        try {
            const { data } = await api.get(this.baseUrl + "/address-suggestion", { params: { postcode } }); 
            return data.suggestions;
        } catch (error) {
            console.error("Error fetching address suggestion", error);
            return null;
        }
    }
}

export default new QuoteService();

export interface InitResponse {
    production: boolean;
    pageState: PageState;
    quoteSteps: { [key: string]: QuoteStep };
    userSetupInfo: UserSetupInfo;
    userDetails: UserDetails;
    branding: Branding;
    quoteInProgress: QuoteRequest;
    lastQuote: Quote;
    lastLLMRiskAnalysis: LLMResponse;
}

export interface LoadNewContentResponse {
    pageState: PageState;
    userSetupInfo: UserSetupInfo;
    userDetails: UserDetails;
    quoteInProgress: QuoteRequest;
    lastQuote: Quote;
    lastLLMRiskAnalysis: LLMResponse;
    token: string;
}

export interface GetQuoteResponse {
    lastQuote?: Quote;
    pageState?: PageState;
}
export interface SendQuoteParameterResponse {
    llmResponse: LLMResponse;
    valid: boolean;
}
export interface CompanyData {
    name: string;
    number: string;
    address: string;
}
export interface AddressSuggestion {
    address: string,
    url: string,
    id: string
}
export interface Address {
    line_1: string,
    line_2: string,
    line_3: string,
    country: string,
    county: string,
    postcode: string
    latitude: number,
    longitude: number,
    town_or_city: string,
}